import * as React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../v32/components/ui/tabs';
import AdminMetrics from '../sections/admin/AdminMetrics';
import AdminSettings from '../sections/admin/AdminSettings';
import { useRequireAdmin } from '../hooks/useRequireAdmin';
import { UI_FLAGS as FLAGS } from '../lib/flags';
import { useQuery } from 'wasp/client/operations';
import { getAdminMetrics } from 'wasp/client/operations';
import { AppShellWrapper } from '../navigation/AppShellWrapper';

export default function AdminPage(): JSX.Element {
  const { isLoading } = useRequireAdmin();
  const { data: metrics } = useQuery(getAdminMetrics);
  
  if (isLoading) return <div className="p-6 text-muted">Loading…</div>;

  return (
    <AppShellWrapper requireAuth={true} requireRole="admin">
      <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="metrics" className="w-full">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold">Admin Dashboard</h1>
            <span className="text-xs px-2 py-1 rounded bg-coral-100 text-coral-700">Phase 1: PMF</span>
          </div>
          <TabsList>
            <TabsTrigger value="metrics">Metrics</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            {FLAGS.enableVerboseLogs && <TabsTrigger value="dev">Dev</TabsTrigger>}
          </TabsList>

          <TabsContent value="metrics">
            <AdminMetrics />
          </TabsContent>

          <TabsContent value="settings">
            <AdminSettings />
          </TabsContent>

          {FLAGS.enableVerboseLogs && (
            <TabsContent value="dev">
              <div className="space-y-4">
                <h2 className="text-lg font-semibold mb-4 text-neutral-800">Developer Tools</h2>
                
                {/* JSON Debug View */}
                <div className="bg-black text-green-300 p-4 rounded-lg font-mono text-xs overflow-auto max-h-96">
                  <div className="text-green-400 mb-2">// Raw Metrics Data</div>
                  <pre>{JSON.stringify(metrics, null, 2)}</pre>
                </div>

                {/* System Info */}
                <div className="bg-neutral-50 p-4 rounded-lg">
                  <h3 className="font-medium text-sm text-neutral-800 mb-3">System Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-neutral-600">Environment:</span>
                      <span className="ml-2 font-mono">{import.meta.env.DEV ? 'development' : 'production'}</span>
                    </div>
                    <div>
                      <span className="text-neutral-600">Timestamp:</span>
                      <span className="ml-2 font-mono">{new Date().toISOString()}</span>
                    </div>
                    <div>
                      <span className="text-neutral-600">User Agent:</span>
                      <span className="ml-2 font-mono truncate">{navigator.userAgent}</span>
                    </div>
                    <div>
                      <span className="text-neutral-600">Memory Usage:</span>
                      <span className="ml-2 font-mono">
                        {(performance as any).memory ? 
                          `${Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024)}MB` : 
                          'N/A'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Feature Flags Debug */}
                <div className="bg-sage-50 p-4 rounded-lg">
                  <h3 className="font-medium text-sm text-sage-800 mb-3">Active Feature Flags</h3>
                  <div className="text-xs font-mono space-y-1">
                    {Object.entries(FLAGS).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-sage-700">{key}:</span>
                        <span className={value ? 'text-sage-800' : 'text-neutral-500'}>
                          {value ? '✓ enabled' : '✗ disabled'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
      </div>
    </AppShellWrapper>
  );
}
